import { put, race, select, take, takeEvery } from "redux-saga/effects";

import SandboxStatus from "@mapmycustomers/shared/enum/SandboxStatus";
import Organization from "@mapmycustomers/shared/types/Organization";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId, getSandbox, isCurrentUserOwner } from "@app/store/iam";
import { fetchOrgSandbox, setOrgSandbox } from "@app/store/iam/actions";
import Integration from "@app/types/integrations/Integration";

import {
  createSandboxConfig,
  deleteSandboxConfig,
  initSandboxPage,
  updateSandboxConfig,
} from "./actions";

export function* onInitSandboxPage() {
  try {
    yield put(fetchOrgSandbox.request());
    const { failure }: { failure?: ReturnType<typeof fetchOrgSandbox.failure> } = yield race({
      failure: take(fetchOrgSandbox.failure),
      success: take(fetchOrgSandbox.success),
    });
    if (failure) {
      throw failure.payload;
    }

    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const isOwner: boolean = yield select(isCurrentUserOwner);

    let hasIntegration = false;
    if (isOwner) {
      const integrationResponse: ListResponse<Integration> = yield callApi(
        "fetchIntegrations",
        organizationId
      );
      hasIntegration = integrationResponse.data.length > 0;
    }
    yield put(initSandboxPage.success({ hasIntegration }));
  } catch (error) {
    yield put(initSandboxPage.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onCreateSandboxConfig({
  payload: { callback, name },
}: ReturnType<typeof createSandboxConfig.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("createSandbox", organizationId, name);
    callback?.();
    yield put(createSandboxConfig.success());
    yield put(initSandboxPage.request());
  } catch (error) {
    yield put(createSandboxConfig.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateSandboxConfig({
  payload,
}: ReturnType<typeof updateSandboxConfig.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("updateSandbox", organizationId, payload);
    yield put(updateSandboxConfig.success());
    yield put(initSandboxPage.request());
  } catch (error) {
    yield put(updateSandboxConfig.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteSandboxConfig() {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteSandbox", organizationId);
    const sandbox: Sandbox | undefined = yield select(getSandbox);
    if (sandbox) {
      yield put(setOrgSandbox({ ...sandbox, status: SandboxStatus.DELETING }));
    }
    yield put(deleteSandboxConfig.success());
  } catch (error) {
    yield put(deleteSandboxConfig.failure(error));
    yield put(handleError({ error }));
  }
}

export function* sandboxSaga() {
  yield takeEvery(initSandboxPage.request, onInitSandboxPage);
  yield takeEvery(createSandboxConfig.request, onCreateSandboxConfig);
  yield takeEvery(updateSandboxConfig.request, onUpdateSandboxConfig);
  yield takeEvery(deleteSandboxConfig.request, onDeleteSandboxConfig);
}
